import { useState } from 'react'
import moment from 'moment'
import { useSnackbarContext } from 'shared/ui'
import { useAppContext } from 'App'
import { getActivityType } from 'apps/contacts/api'
import { useDispatch, refreshEvent } from '../store/actions'

// Event type -> the v1 Activity Type code it logs as (same map the Lead Detail timeline's
// Log activity uses — apps/leads/pages/LeadDetail/ActivitiesSection.js).
const EVENT_TYPE_TO_ACTIVITY_CODE = {
  meeting: 'meeting',
  call: 'phone-call',
  email: 'email',
}

// Available as soon as the event has started — a logged activity is always "completed",
// which can't be in the future (same rule as the Lead Detail timeline's Log activity).
export const hasEventStarted = (event) => Boolean(event?.starts_at) && !moment(event.starts_at).isAfter(moment())

// Log Activity for any calendar event, from wherever it's listed (the detail sidebar, the
// daily sidebar, the agenda). Opens the Contacts module's Add Activity modal
// (appContext.showModalView is store-agnostic, so it's safe from this module's isolated
// store), prefilled with the event's type/time/assignee, and linked to this event — plus the
// lead and issue the event is linked to, if any.
//   const { logActivity, isFetching } = useEventLogActivity()
//   logActivity(event)
export function useEventLogActivity() {
  const appContext = useAppContext()
  const { showSnackbar } = useSnackbarContext()
  const dispatch = useDispatch()
  const [isFetching, setIsFetching] = useState(false)

  const open = (event, types) => {
    const matchedType = types.find(t => t.code === EVENT_TYPE_TO_ACTIVITY_CODE[event?.event_type]) || {}
    const hasTopicField = matchedType?.fields?.some(f => f.key === 'topic')
    const leadLink = (event?.links || []).find(l => l.target_object_type === 'lead/Lead')
    const issueLink = (event?.links || []).find(l => l.target_object_type === 'project/Issue')

    appContext.showModalView('contacts', 'add-activity', {
      showContactAfterCreate: false,
      activityType: matchedType,
      ...(leadLink ? {
        leadDetail: {
          ...(leadLink.target_object_data || {}),
          uid: Number(leadLink.target_object_uid),
          name: leadLink.target_object_data?.name || leadLink.title,
        },
      } : {}),
      // The modal takes one lead and one issue — the event's first of each.
      ...(issueLink ? {
        issueDetail: {
          ...(issueLink.target_object_data || {}),
          uid: Number(issueLink.target_object_uid),
          key: issueLink.target_object_data?.key,
          name: issueLink.target_object_data?.name || issueLink.title,
        },
        projectKey: issueLink.target_object_data?.project?.key,
      } : {}),
      eventDetail: event,
      agentKey: event?.assigned_to?.uid,
      // Called by the modal once the activity has actually been created.
      completeCallback: () => {
        showSnackbar('Activity logged', {
          variant: 'success',
          anchorOrigin: { vertical: 'top', horizontal: 'right' },
        })
        // Silently re-fetch the event so its `activities` include the one just logged.
        dispatch(refreshEvent(event?.uid))
      },
      activityDetail: {
        activity_time: event?.starts_at,
        ...(hasTopicField ? { topic: event?.name || '' } : {}),
      },
    })
  }

  const logActivity = (event) => {
    if (isFetching) return
    setIsFetching(true)
    getActivityType({ fields: 'fields' }).then(({ ok, data: types }) => {
      setIsFetching(false)
      if (ok && types) open(event, types)
    })
  }

  return { logActivity, isFetching }
}

export default useEventLogActivity
